import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

/** Staff screens: Arabic (default, RTL) + English. Drivers' pages (S3) are Arabic only. */
export type Lang = 'ar' | 'en';

const ar = {
  brand: 'JABAL VIP',
  'nav.week': 'الأسبوع',
  'nav.drivers': 'السائقون',
  'nav.upload': 'رفع الجرد',
  'nav.settings': 'الإعدادات',
  owes: 'عليه',
  owed: 'له',
  even: 'صفر',
  jd: 'دينار',
  loading: 'جارٍ التحميل…',
  retry: 'إعادة المحاولة',
  back: 'رجوع',
  save: 'حفظ',
  saved: 'تم الحفظ.',
  cancel: 'إلغاء',
  'err.generic': 'حدث خطأ. يُرجى المحاولة مرة أخرى.',
  'err.BAD_LOGIN': 'رقم الهاتف أو كلمة المرور غير صحيحة.',
  'err.BAD_PHONE': 'رقم الهاتف غير صحيح.',
  'err.TOO_MANY_TRIES': 'محاولات كثيرة. يُرجى الانتظار ربع ساعة ثم المحاولة مجددًا.',
  'err.TOO_MANY_CODES': 'طلبتَ رموزًا كثيرة. يُرجى الانتظار ساعة ثم المحاولة مجددًا.',
  'err.BAD_CODE': 'الرمز غير صحيح.',
  'err.CODE_EXPIRED': 'انتهت صلاحية الرمز. ارجع وابدأ من جديد.',
  'err.NOT_A_WORKBOOK': 'هذا الملف ليس ملف إكسل (.xlsx).',
  'err.NO_WEEK_TABS': 'لم نجد أي ورقة «جرد» في الملف.',
  'err.FILE_TOO_BIG': 'حجم الملف أكبر من 25 ميغابايت.',
  'err.EMPTY_FILE': 'الملف فارغ.',
  'err.BAD_TAB': 'تعذّرت قراءة هذه الورقة.',
  'err.ALREADY_PUBLISHED': 'هذه النسخة منشورة مسبقًا.',
  'err.NOT_PREVIEWED': 'افحص الملف قبل النشر.',
  'err.WEEK_CLOSED': 'هذا الأسبوع مُغلق.',
  'err.NOT_LOGGED_IN': 'انتهت الجلسة. يُرجى تسجيل الدخول من جديد.',
  'err.SERVER_DOWN': 'الخادم لا يعمل. على جهاز التطوير: تأكّد من تشغيل pnpm dev وعدم ظهور أي خطأ في الطرفية.',
  'err.OFFLINE': 'لا يوجد اتصال بالإنترنت.',

  'login.title': 'دخول المحاسب',
  'login.phone': 'رقم الهاتف',
  'login.password': 'كلمة المرور',
  'login.continue': 'متابعة',
  'login.codeTitle': 'رمز واتساب',
  'login.codeSent': 'أرسلنا رمزًا من 6 أرقام عبر واتساب إلى الرقم المنتهي بـ {n}.',
  'login.code': 'الرمز',
  'login.verify': 'دخول',
  'login.devCode': 'وضع التطوير — الرمز: {code}',

  'week.title': 'جرد {tab}',
  'week.range': 'الخميس {from} ← الخميس {to}',
  'week.status.DRAFT': 'مسودة',
  'week.status.PUBLISHED': 'منشور',
  'week.status.CLOSED': 'مُغلق',
  'week.version': 'النسخة {v}',
  'week.deadline': 'آخر موعد للدفع',
  'week.editDeadline': 'تعديل الموعد',
  'week.oweTotal': 'المستحق على السائقين',
  'week.owedTotal': 'المستحق للسائقين',
  'week.fees': 'الاشتراكات',
  'week.drivers': 'عدد السائقين: {n}',
  'week.emptyTitle': 'ابدأ بالأسبوع الأول',
  'week.empty': 'لا توجد أسابيع بعد. ارفع ملف الجرد للبدء.',
  'week.notPublished': 'لم يُنشر هذا الأسبوع بعد.',
  'week.upload': 'رفع ملف الجرد',
  'week.reupload': 'رفع نسخة معدّلة',
  'week.problems': 'ملاحظات في الملف: {n}',
  'week.changed': 'تغيّروا في آخر نسخة: {n}',
  'week.otherWeeks': 'أسابيع أخرى',
  'filter.all': 'الكل',
  'filter.owes': 'عليهم مبالغ',
  'filter.owed': 'لهم مبالغ',
  'filter.fee': 'أخذوا طلبات',
  'filter.changed': 'تغيّروا',
  'filter.problems': 'ملاحظات',
  search: 'ابحث بالاسم أو الرقم…',
  skip: 'انتقل إلى المحتوى',
  'search.none': 'لا توجد نتائج.',
  'list.lines': 'عدد البنود: {n}',
  'list.exempt': 'مُعفى',
  'list.removed': 'أُزيل',

  'st.title': 'كشف حساب',
  'st.week': 'جرد {tab} · {from} – {to}',
  'st.credit': 'إنتاج',
  'st.debit': 'استهلاك',
  'st.fee': 'اشتراك الأسبوع',
  'st.total': 'المجموع',
  'st.toPay': 'المطلوب منه',
  'st.toReceive': 'المستحق له',
  'st.zero': 'لا شيء عليه ولا له',
  'st.row': 'السطر {n}',
  'st.noDetails': 'بلا تفاصيل',
  'st.driverPage': 'صفحة السائق',
  'st.sheetStatus': 'الحالة: {s}',

  'drv.title': 'جميع السائقين',
  'drv.weeks': 'الأسابيع',
  'drv.noWeeks': 'لا توجد له كشوفات منشورة.',
  'drv.profile': 'بيانات السائق',
  'drv.nameInSheet': 'اسمه في الجرد: {name}',
  'drv.displayName': 'الاسم المعروض',
  'drv.displayNameHint': 'اتركه فارغًا لاستخدام اسمه في الجرد.',
  'drv.feeExempt': 'مُعفى من الاشتراك (مشرف)',
  'drv.removed': 'أُزيل من المجموعة',
  'drv.notes': 'ملاحظات (لا يراها السائق)',
  'drv.whatsapp': 'واتساب',

  'up.title': 'رفع ملف الجرد',
  'up.pick': 'اختر ملف الإكسل',
  'up.hint': 'ارفع الملف كاملًا كما هو، وستُقرأ منه أوراق «جرد» فقط.',
  'up.reading': 'جارٍ قراءة الملف…',
  'up.whichWeek': 'أي أسبوع؟',
  'up.newest': 'الأحدث',
  'up.published': 'منشور مسبقًا — ستنشر نسخة معدّلة.',
  'up.check': 'فحص الأسطر',
  'up.checking': 'جارٍ فحص الأسطر…',
  'up.lines': 'أسطر تحتوي على مبالغ: {n}',
  'up.empty': 'أسطر بلا مبالغ: {n}',
  'up.rosterOnly': 'مُدرَجون بلا مبالغ هذا الأسبوع (عليهم الرسوم فقط): {n}',
  'up.newDrivers': 'سائقون جدد: {n}',
  'up.problems': 'قبل النشر',
  'up.clean': 'الملف سليم ولا توجد ملاحظات.',
  'up.sev.error': 'مهم',
  'up.sev.warning': 'للتحقق',
  'up.sev.info': 'للعلم',
  'up.rows': 'الأسطر: {rows}',
  'up.more': 'و{n} أخرى',
  'up.changes': 'ما الذي تغيّر عن النسخة المنشورة',
  'up.noChanges': 'مطابقة تمامًا للنسخة المنشورة.',
  'up.change.added': 'جديد',
  'up.change.removed': 'أُزيل',
  'up.change.changed': 'تغيّر',
  'up.deadline': 'آخر موعد للدفع',
  'up.publish': 'نشر الكشوفات',
  'up.publishing': 'جارٍ النشر…',
  'up.done': 'نُشرت الكشوفات',
  'up.doneBody': 'كشوفات جرد {tab} جاهزة (النسخة {v}). سيُضاف إرسالها إلى السائقين عبر واتساب في مرحلة لاحقة.',
  'up.viewWeek': 'عرض الأسبوع',
  'up.another': 'رفع ملف آخر',
  'up.change': 'تغيير',

  'set.title': 'الإعدادات',
  'set.money': 'الحساب',
  'set.visibility': 'ما يراه السائقون والمنتجون',
  'set.oldWeeks': 'إظهار الكشوف السابقة',
  'set.oldWeeksHint':
    'عند إيقافه يرى كلٌّ منهم كشف الأسبوع الأحدث فقط، حتى لو بقي على السائق مبلغ من أسبوع سابق (يُسوّى ذلك من صفحة السائق هنا).',
  'set.fee': 'الاشتراك الأسبوعي',
  'set.feeHint': 'يُفرض على كل رقم في ورقة الأسبوع، ويُعفى منه المشرفون والمنتجون.',
  'set.deadline': 'آخر موعد للدفع',
  'set.deadlineHint': 'بعد إغلاق الأسبوع (الخميس الساعة 12 ظهرًا). يُطبَّق عند الحفظ على كل أسبوع لم ينتهِ موعده بعد، وعلى الأسابيع الجديدة.',
  'set.autoFine': 'غرامة التأخير التلقائية',
  'set.autoFineHint': 'تُنسخ إلى كل أسبوع عند نشره أول مرة، ويمكن تعديلها لكل أسبوع من شريط الأسبوع. 0 = بلا غرامة.',
  'set.payMethods': 'طرق الدفع',
  'set.payMethodsHint': 'هذه هي الطرق التي يراها السائق عند الدفع.',
  'set.addMethod': 'إضافة طريقة دفع',
  'set.type.CLIQ': 'كليك',
  'set.type.ZAIN_CASH': 'زين كاش',
  'set.type.ORANGE_MONEY': 'أورنج موني',
  'set.type.OTHER': 'أخرى',
  'set.label': 'الاسم',
  'set.value': 'الاسم المستعار أو الرقم',
  'set.shown': 'ظاهرة للسائقين',
  'set.hidden': 'مخفية',
  'set.hide': 'إخفاء',
  'set.show': 'إظهار',
  'set.delete': 'حذف',
  'set.deleteTitle': 'هل تريد حذف طريقة الدفع «{label}»؟',
  'set.deleteBody': 'لن تظهر للسائقين بعد ذلك. يمكنك إخفاؤها بدلًا من حذفها.',
  'set.up': 'نقل إلى الأعلى',
  'set.appearance': 'المظهر واللغة',
  'set.dark': 'الوضع الداكن',
  'set.english': 'English',
  'set.activity': 'سجل التغييرات',
  'set.account': 'الحساب',
  'set.logout': 'تسجيل الخروج',
  'day.0': 'الخميس',
  'day.1': 'الجمعة',
  'day.2': 'السبت',
  'day.3': 'الأحد',
  'day.4': 'الاثنين',
  'day.5': 'الثلاثاء',
  'day.6': 'الأربعاء',
  'day.7': 'الخميس التالي',

  'act.title': 'سجل التغييرات',
  'act.more': 'عرض الأقدم',
  'act.staff.add': 'إضافة حساب موظف',
  'act.staff.login': 'تسجيل دخول',
  'act.staff.password_reset': 'تغيير كلمة المرور',
  'act.upload.create': 'رفع ملف',
  'act.upload.preview': 'فحص ورقة',
  'act.week.publish': 'نشر كشوفات',
  'act.week.deadline': 'تغيير موعد الدفع',
  'act.driver.update': 'تعديل بيانات سائق',
  'act.settings.update': 'تعديل الإعدادات',
  'act.paymethod.add': 'إضافة طريقة دفع',
  'act.paymethod.update': 'تعديل طريقة دفع',
  'act.paymethod.delete': 'حذف طريقة دفع',
  'nav.home': 'الرئيسية',
  'nav.review': 'المراجعة',
  'nav.search': 'بحث',
  'status.NONE': 'لا شيء عليه',
  'status.PAID': 'مدفوع',
  'status.SUBMITTED': 'أرسل إثباتًا',
  'status.DISPUTED': 'معترض',
  'status.LATE': 'متأخر',
  'status.UNPAID': 'لم يدفع',
  'home.title': 'نظرة عامة',
  'home.collected': 'تم تحصيله',
  'home.remaining': 'المتبقي للتحصيل',
  'home.progress': 'دفع {a} من {b} سائقًا',
  'home.todo': 'بانتظارك',
  'home.proofs': 'إثباتات دفع بانتظار المراجعة',
  'home.disputes': 'اعتراضات مفتوحة',
  'home.late': 'سائقون متأخرون عن الدفع',
  'home.unpaid': 'لم يدفعوا بعد',
  'home.notes': 'ملاحظات في ملف الجرد',
  'home.allClear': 'لا يوجد ما ينتظرك الآن.',
  'home.quick': 'اختصارات',
  'home.allDrivers': 'جدول السائقين',
  'tbl.scope': 'النطاق',
  'tbl.all': 'جميع السائقين (كل الأسابيع)',
  'tbl.weekOption': 'جرد {tab} · {from}',
  'tbl.name': 'السائق',
  'tbl.status': 'الحالة',
  'tbl.lines': 'البنود',
  'tbl.credit': 'إنتاج',
  'tbl.debit': 'استهلاك',
  'tbl.fee': 'الاشتراك',
  'tbl.total': 'المجموع',
  'tbl.remaining': 'المتبقي',
  'tbl.outstanding': 'المستحق عليه',
  'tbl.lastWeek': 'آخر أسبوع',
  'tbl.weeks': 'الأسابيع',
  'tbl.actions': 'إجراءات',
  'tbl.sort': 'الترتيب',
  'tbl.count': 'عدد النتائج: {n}',
  'tbl.clear': 'مسح الفلاتر',
  'tbl.lateWeeks': 'أسابيع متأخرة: {n}',
  'sort.amount': 'المبلغ (الأعلى أولًا)',
  'sort.name': 'الاسم',
  'sort.status': 'الحالة',
  'sort.lines': 'عدد البنود',
  'f.unpaid': 'لم يدفعوا',
  'f.submitted': 'أرسلوا إثباتًا',
  'f.paid': 'دفعوا',
  'f.late': 'متأخرون',
  'f.disputed': 'معترضون',
  'f.exempt': 'معفون',
  'f.removed': 'أُزيلوا',
  'f.outstanding': 'عليهم مستحقات',
  'row.statement': 'عرض الكشف',
  'row.profile': 'صفحة السائق',
  'row.whatsapp': 'مراسلة عبر واتساب',
  'row.copyPhone': 'نسخ الرقم',
  'ms.open': 'الإرسال من هاتفي',
  'ms.title': 'الإرسال من هاتفك',
  'ms.statement': 'الكشوف',
  'ms.reminder': 'التذكير',
  'ms.how':
    'اضغط «إرسال عبر واتساب» فيُفتح واتساب والرسالة جاهزة، ثم اضغط إرسال هناك وارجع إلى هذه الصفحة، فتجد التالي جاهزًا. في كل رسالة رابط دخول لمرة واحدة، فلا يحتاج السائق إلى رمز. أرسل على دفعات مع استراحات بينها، حتى لا يُحظر رقمك.',
  'ms.progress': 'أُرسلت: {n} من {total}',
  'ms.showText': 'نص الرسالة',
  'ms.send': 'إرسال عبر واتساب',
  'ms.skip': 'تخطٍّ',
  'ms.skipped': 'تُخطّي',
  'ms.sent': 'أُرسلت',
  'ms.sentBefore': 'أُرسلت من قبل',
  'ms.done': 'أُرسلت الرسالة إلى الجميع.',
  'ms.nobody': 'لا يوجد من يحتاج هذه الرسالة في هذا الأسبوع.',
  'ms.skippedLeft': 'عدد من تخطّيتهم: {n}',
  'ms.backToSkipped': 'العودة إلى من تخطّيتهم',
  'ms.everyone': 'الجميع',
  'sl.title': 'دخول الموظفين',
  'sl.hint': 'إذا تعذّر إرسال رمز الدخول عبر واتساب، أرسل إلى الموظف رابط دخول لمرة واحدة، فيدخل به مع كلمة مروره.',
  'sl.link': 'رابط دخول',
  'sl.ACCOUNTANT': 'محاسب',
  'sl.SUPER_ADMIN': 'مسؤول',
  'll.bodyStaff': 'يدخل به إلى لوحة التحكم مع كلمة مروره، دون رمز واتساب. يعمل مرة واحدة فقط، وينتهي في {until}.',
  'll.messageStaff': 'رابط الدخول إلى لوحة JABAL VIP (يعمل مرة واحدة، مع كلمة مرورك):\n{url}',
  'err.NOT_A_RECIPIENT': 'هذا السائق ليس ضمن مستلمي هذه الرسالة.',
  'err.NO_STAFF': 'هذا الحساب غير موجود.',
  'act.staff.loginLink': 'رابط دخول لموظف',
  'll.make': 'رابط دخول لمرة واحدة',
  'll.title': 'رابط دخول: {name}',
  'll.body': 'يدخل به مباشرةً دون رمز واتساب. يعمل مرة واحدة فقط، وينتهي في {until}. إنشاء رابط جديد يُبطل هذا الرابط.',
  'll.link': 'الرابط',
  'll.sendTo': 'أرسله إلى الرقم:',
  'll.copy': 'نسخ الرابط',
  'll.copied': 'نُسخ رابط الدخول.',
  'll.whatsapp': 'إرسال عبر واتساب',
  'll.message': 'رابط الدخول إلى كشفك في JABAL VIP (يعمل مرة واحدة):\n{url}',
  'act.driver.loginLink': 'رابط دخول لسائق',
  'act.producer.loginLink': 'رابط دخول لمنتج',
  'row.copyLink': 'نسخ رابط السائق',
  'row.cash': 'تسجيل دفعة نقدية',
  copied: 'تم النسخ.',
  'link.copied': 'نُسخ رابط السائق. أرسله له عبر واتساب.',
  'link.regenerate': 'إنشاء رابط جديد',
  'link.regenerateBody': 'سيتوقف الرابط القديم عن العمل. استخدم هذا إذا وصل الرابط إلى شخص آخر.',
  'link.regenerated': 'أُنشئ رابط جديد ونُسخ.',
  'cash.title': 'تسجيل دفعة نقدية',
  'cash.body': 'سيُسجَّل أن {name} دفع {amount} دينار عن جرد {tab}.',
  'cash.note': 'ملاحظة (اختياري)',
  'cash.confirm': 'تسجيل الدفعة',
  'cash.done': 'سُجّلت الدفعة.',
  'rev.proofs': 'إثباتات الدفع',
  'rev.disputes': 'الاعتراضات',
  'rev.waiting': 'بانتظار المراجعة',
  'rev.history': 'السجل',
  'rev.emptyProofs': 'لا توجد إثباتات بانتظار المراجعة.',
  'rev.emptyDisputes': 'لا توجد اعتراضات مفتوحة.',
  'rev.emptyHistory': 'لا يوجد سجل بعد.',
  'rev.pick': 'اختر عنصرًا من القائمة لمراجعته.',
  'rev.amount': 'المبلغ',
  'rev.method': 'طريقة الدفع',
  'rev.reference': 'رقم الحوالة',
  'rev.sentAt': 'أُرسل',
  'rev.approve': 'قبول',
  'rev.reject': 'رفض',
  'rev.reason': 'سبب الرفض',
  'rev.reasonPlaceholder': 'اكتب السبب ليظهر للسائق…',
  'rev.r1': 'الصورة غير واضحة',
  'rev.r2': 'المبلغ غير صحيح',
  'rev.r3': 'لم تصل الحوالة',
  'rev.r4': 'الصورة مكررة',
  'rev.reused': 'هذه الصورة استُخدمت في دفعة أخرى — تحقّق منها.',
  'rev.approved': 'قُبلت الدفعة.',
  'rev.rejected': 'رُفضت الدفعة.',
  'rev.reopen': 'إرجاع إلى المراجعة',
  'rev.reopened': 'أُرجعت الدفعة إلى المراجعة.',
  'rev.openImage': 'فتح الصورة بالحجم الكامل',
  'rev.line': 'البند المعترض عليه',
  'rev.message': 'رسالة السائق',
  'rev.reply': 'الرد على السائق',
  'rev.replyPlaceholder': 'اكتب ردك ليظهر للسائق…',
  'rev.accept': 'قبول الاعتراض',
  'rev.rejectDispute': 'رفض الاعتراض',
  'rev.acceptHint': 'بعد القبول: عدّل ملف الإكسل وارفعه من جديد ليتغيّر الكشف.',
  'rev.answered': 'أُرسل الرد.',
  'rev.keys': 'اختصارات لوحة المفاتيح',
  'rev.photo': 'صورة مرفقة',
  'pstatus.SUBMITTED': 'بانتظار المراجعة',
  'pstatus.APPROVED': 'مقبولة',
  'pstatus.REJECTED': 'مرفوضة',
  'dstatus.OPEN': 'مفتوح',
  'dstatus.ACCEPTED': 'مقبول',
  'dstatus.REJECTED': 'مرفوض',
  'dstatus.WITHDRAWN': 'مسحوب',
  'method.CASH': 'نقدًا',
  'method.CLIQ': 'كليك',
  'method.ZAIN_CASH': 'زين كاش',
  'method.ORANGE_MONEY': 'أورنج موني',
  'method.OTHER': 'أخرى',
  'drv.tabs.weeks': 'الكشوفات',
  'drv.tabs.payments': 'الدفعات',
  'drv.tabs.disputes': 'الاعتراضات',
  'drv.tabs.profile': 'البيانات',
  'drv.outstanding': 'المستحق عليه في كل الأسابيع',
  'drv.noPayments': 'لا توجد دفعات.',
  'drv.noDisputes': 'لا توجد اعتراضات.',
  'st.payment': 'الدفع',
  'st.paidOf': 'دُفع {paid} من {due} دينار',
  'st.disputedLine': 'عليه اعتراض',
  'pal.placeholder': 'ابحث عن سائق بالاسم أو الرقم، أو عن صفحة…',
  'pal.pages': 'الصفحات',
  'pal.drivers': 'السائقون',
  'pal.weeks': 'الأسابيع',
  'pal.empty': 'لا توجد نتائج.',
  'pal.title': 'البحث السريع',
  'nav.activity': 'سجل التغييرات',
  'tbl.copyPhones': 'نسخ الأرقام',
  'tbl.export': 'تصدير CSV',
  'nav.send': 'إرسال الكشوف',
  'nav.removal': 'قائمة الإزالة',
  'send.title': 'الإرسال عبر واتساب',
  'send.week': 'الأسبوع',
  'send.kind': 'نوع الرسالة',
  'send.kind.STATEMENT': 'الكشف الأسبوعي',
  'send.kind.REMINDER': 'تذكير بالدفع',
  'send.kind.DISPUTE_REPLY': 'رد على اعتراض',
  'send.audience': 'المستلمون',
  'send.aud.ALL': 'الجميع',
  'send.aud.CHANGED': 'من تغيّر كشفه',
  'send.aud.UNPAID': 'من لم يدفع',
  'send.audHint.ALL': 'كل سائق له كشف في هذا الأسبوع.',
  'send.audHint.CHANGED': 'من لم تصله رسالة بعد، أو تغيّر مجموعه بعد آخر رسالة (بعد إعادة الرفع مثلًا).',
  'send.audHint.UNPAID': 'من عليه مبلغ ولم يرسل إثباتًا وليس لديه اعتراض مفتوح.',
  'send.reminderHint': 'يصل التذكير إلى كل سائق مرة واحدة فقط في الأسبوع، ولا يصل إلى من دفع أو أرسل إثباتًا أو لديه اعتراض مفتوح.',
  'send.pace': 'سرعة الإرسال',
  'send.pace.SLOW': 'بطيء (موصى به)',
  'send.paceHint.SLOW': 'تُرسل رسالة كل 30 إلى 45 ثانية حمايةً للرقم من الحظر.',
  'send.pace.FAST': 'سريع',
  'send.paceHint.FAST': 'رسالة كل 3 إلى 5 ثوانٍ.',
  'send.fastWarn': 'قد يحظر واتساب الرقم عند إرسال عدد كبير من الرسائل بسرعة. استخدم الإرسال السريع لعدد قليل من السائقين فقط.',
  'send.template': 'نص الرسالة',
  'send.templateHint': 'اضغط على أي متغير لإضافته. يُحذف السطر تلقائيًا إذا كان متغيره فارغًا، مثل سطر آخر موعد للدفع لمن لا يُطلب منه شيء.',
  'send.var.name': 'الاسم',
  'send.var.number': 'رقم الهاتف',
  'send.var.week': 'الأسبوع',
  'send.var.amount': 'المبلغ',
  'send.var.deadline': 'آخر موعد',
  'send.var.link': 'الرابط',
  'send.needsLink': 'يجب أن يحتوي النص على رابط السائق.',
  'send.saveDefault': 'حفظ كنص افتراضي',
  'send.savedDefault': 'حُفظ النص الافتراضي.',
  'send.resetDefault': 'استعادة النص المحفوظ',
  'send.preview': 'معاينة الرسالة',
  'send.previewFor': 'كما ستصل إلى: {name}',
  'send.count': 'عدد المستلمين: {n}',
  'send.minutes': 'المدة التقريبية بالدقائق: {n}',
  'send.skipped': 'وصلهم تذكير هذا الأسبوع فلن يُرسَل إليهم (العدد: {n}).',
  'send.none': 'لا يوجد من يُرسَل إليه.',
  'send.start': 'بدء الإرسال',
  'send.confirmTitle': 'هل تريد بدء الإرسال؟',
  'send.confirmBody': 'عدد الرسائل: {n}. المدة التقريبية بالدقائق: {m}. يمكنك الإيقاف المؤقت في أي وقت.',
  'send.started': 'بدأ الإرسال.',
  'send.now': 'الإرسال الحالي',
  'send.progress': 'تم إرسال {a} من {b}',
  'send.pause': 'إيقاف مؤقت',
  'send.resume': 'متابعة',
  'send.cancel': 'إلغاء المتبقي',
  'send.retry': 'إعادة إرسال الفاشلة',
  'send.continue': 'متابعة الإرسال لمن لم تصله',
  'send.continueHint': 'لم تصلهم الرسالة بعد (العدد: {n}). تُرسَل إليهم فقط، بالأرقام الحالية، ويُستثنى من لم يعد بحاجة إليها (من دفع مثلًا).',
  'send.continued': 'استؤنف الإرسال.',
  'err.NOT_CANCELLED': 'هذا الإرسال لم يُلغَ.',
  'send.cancelTitle': 'هل تريد إلغاء الرسائل المتبقية؟',
  'send.cancelBody':
    'تتوقف الرسائل التي لم تُرسَل بعد، ولا يتأثر ما أُرسل. يمكنك متابعة الإرسال لاحقًا من «الإرسالات السابقة»، فيُرسَل فقط إلى من لم تصله.',
  'send.paused.DISCONNECTED': 'توقف الإرسال لأن واتساب غير متصل. تأكّد من اتصال الرقم ثم اضغط «متابعة».',
  'send.paused.FAILURES': 'توقف الإرسال بعد فشل عدة رسائل متتالية. راجع الأخطاء ثم اضغط «متابعة».',
  'send.paused.STAFF': 'الإرسال متوقف مؤقتًا.',
  'send.b.RUNNING': 'جارٍ الإرسال',
  'send.b.PAUSED': 'متوقف مؤقتًا',
  'send.b.DONE': 'اكتمل',
  'send.b.CANCELLED': 'أُلغي',
  'send.m.QUEUED': 'في الانتظار',
  'send.m.SENDING': 'يُرسَل الآن',
  'send.m.SENT': 'أُرسلت',
  'send.m.DELIVERED': 'وصلت',
  'send.m.READ': 'قُرئت',
  'send.m.FAILED': 'فشلت',
  'send.m.CANCELLED': 'أُلغيت',
  'send.e.NOT_ON_WHATSAPP': 'الرقم غير مسجّل على واتساب',
  'send.e.INTERRUPTED': 'انقطع الإرسال بسبب إعادة تشغيل الخادم',
  'send.e.other': 'تعذّر الإرسال',
  'send.history': 'الإرسالات السابقة',
  'send.historyEmpty': 'لم يُرسَل شيء لهذا الأسبوع بعد.',
  'send.messages': 'الرسائل',
  'send.failedList': 'الرسائل الفاشلة',
  'send.wa.open': 'واتساب متصل',
  'send.wa.down': 'واتساب غير متصل: لن تُرسَل أي رسالة حتى يعود الاتصال.',
  'send.wa.console': 'وضع التطوير: لا تُرسَل الرسائل فعليًا، بل تُطبع في سجل الخادم.',
  'wa.title': 'رقم الإرسال',
  'wa.hint': 'الرقم الذي تصل منه إلى السائقين رموزُ الدخول والكشوفُ والتذكيرات.',
  'wa.open': 'إدارة رقم الإرسال',
  'wa.status': 'حالة الربط',
  'wa.linked': 'مربوط',
  'wa.notLinked': 'غير مربوط',
  'wa.number': 'الرقم',
  'wa.name': 'الاسم في واتساب',
  'wa.downTitle': 'واتساب غير مربوط',
  'wa.downHint': 'لا تُرسَل الآن أي رسالة إلى السائقين، ولا تصل رموز الدخول إلى المحاسب ولا إلى السائقين.',
  'wa.linkNow': 'ربط واتساب الآن',
  'wa.unlink': 'إلغاء الربط',
  'wa.unlinkTitle': 'إلغاء ربط رقم الإرسال؟',
  'wa.unlinkBody': 'لن تُرسَل رموز الدخول ولا الكشوف ولا التذكيرات حتى يُربط الرقم من جديد، ويتوقف أي إرسال جارٍ.',
  'wa.unlinked': 'أُلغي ربط الرقم.',
  'wa.linkTitle': 'ربط الرقم',
  'wa.linkHint': 'أبقِ الهاتف الذي يحمل رقم الإرسال بين يديك، ثم اختر طريقة الربط.',
  'wa.byCode': 'برمز الربط',
  'wa.byQr': 'بمسح رمز QR',
  'wa.phone': 'رقم الإرسال',
  'wa.phoneHint': 'الرقم المسجّل في واتساب على ذلك الهاتف.',
  'wa.getCode': 'احصل على رمز الربط',
  'wa.newCode': 'رمز جديد',
  'wa.getQr': 'اعرض رمز QR',
  'wa.copy': 'نسخ الرمز',
  'wa.qrAlt': 'رمز QR لربط واتساب',
  'wa.step1': 'افتح واتساب على ذلك الهاتف، ثم: الإعدادات ← الأجهزة المرتبطة ← ربط جهاز.',
  'wa.step2code': 'اختر «الربط برقم الهاتف بدلًا من ذلك»، ثم أدخل الرمز الظاهر أعلاه.',
  'wa.step2qr': 'وجّه كاميرا الهاتف نحو الرمز الظاهر أعلاه.',
  'wa.waiting': 'بانتظار إتمام الربط على الهاتف…',
  'wa.expires': 'صلاحية الرمز قصيرة؛ إن انتهت فاطلب رمزًا جديدًا.',
  'wa.done': 'تم ربط واتساب.',
  'login.serverLog': 'تعذّر إرسال الرمز عبر واتساب، فحُفظ في سجل الخادم. اطلبه من المسؤول، فهو يعرضه بتشغيل هذا الأمر على الخادم:',
  'err.CODE_NOT_SENT': 'تعذّر إرسال الرمز عبر واتساب الآن. يُرجى المحاولة لاحقًا.',
  'err.ALREADY_LINKED': 'الرقم مربوط بالفعل.',
  'err.DEV_SENDER': 'وضع التطوير: لا يوجد واتساب لربطه.',
  'err.WHATSAPP_UNREACHABLE': 'تعذّر الوصول إلى خدمة واتساب على الخادم. حاول بعد قليل.',
  'err.FORBIDDEN': 'هذا الإجراء من صلاحيات المشرف العام فقط.',
  'send.noWeek': 'لا يوجد أسبوع منشور بعد. انشر الجرد أولًا.',
  'home.notSent': 'لم تصلهم كشوف هذا الأسبوع',
  'home.toRemove': 'للإزالة من المجموعة',
  'home.sending': 'جارٍ إرسال الكشوف',
  'fine.add': 'إضافة غرامة',
  'fine.title': 'غرامة على {name}',
  'fine.amount': 'المبلغ بالدينار',
  'fine.reason': 'السبب',
  'fine.reasonPh': 'مثال: إلغاء رحلة دون إبلاغ',
  'fine.where': 'تُضاف إلى كشف أسبوع الجرد {tab}، ويراها السائق فيه.',
  'fine.added': 'أُضيفت الغرامة.',
  'fine.cancel': 'إلغاء الغرامة',
  'fine.cancelTitle': 'هل تريد إلغاء هذه الغرامة؟',
  'fine.cancelBody': 'تُخصم من المبلغ المطلوب. الغرامة التلقائية بعد إلغائها لا تُضاف مرة أخرى.',
  'fine.cancelled': 'أُلغيت الغرامة.',
  'fine.line': 'غرامة: {reason}',
  'fine.auto': 'تلقائية',
  'fine.weekFine': 'غرامة التأخير لهذا الأسبوع (دينار)',
  'fine.weekFineHint': 'تُضاف تلقائيًا عند آخر موعد لمن لم يدفع ولم يرسل إثباتًا وليس لديه اعتراض مفتوح. 0 = بلا غرامة.',
  'set.messages': 'رسائل واتساب',
  'set.messagesHint': 'نص الرسائل وسرعة الإرسال تُضبط من صفحة الإرسال.',
  'set.openSend': 'فتح صفحة الإرسال',
  'f.notSent': 'لم يصله الكشف',
  'err.BATCH_ACTIVE': 'يوجد إرسال آخر قيد التنفيذ. انتظر انتهاءه أو ألغِه أولًا.',
  'err.NO_RECIPIENTS': 'لا يوجد من يُرسَل إليه.',
  'err.BATCH_FINISHED': 'انتهى هذا الإرسال.',
  'err.NOTHING_FAILED': 'لا توجد رسائل فاشلة.',
  'err.NOT_PUBLISHED': 'هذا الأسبوع غير منشور.',
  'err.NO_FINE': 'الغرامة غير موجودة أو أُلغيت مسبقًا.',
  'err.NO_STATEMENT': 'لا يوجد كشف لهذا السائق في هذا الأسبوع.',
  'err.INVALID': 'تحقّق من البيانات المدخلة.',
  'rm.title': 'قائمة الإزالة من المجموعة',
  'rm.hint':
    'من بقي عليه مبلغ لأسبوع بعد انتهاء آخر موعد للدفع. حدّد من تريد إزالته، فيُزيله رقم الإرسال من مجموعة واتساب، واحدًا كل بضع ثوانٍ.',
  'rm.deadline': 'انتهى آخر موعد للدفع: {date}',
  'rm.group': 'المجموعة: {name}',
  'rm.noGroup': 'لم تُحدَّد مجموعة واتساب بعد، فلا تمكن الإزالة. يحددها المشرف العام من صفحة «رقم الإرسال».',
  'rm.noGroupAdmin': 'اختر مجموعة واتساب أولًا من صفحة «رقم الإرسال».',
  'rm.chooseGroup': 'اختيار المجموعة',
  'rm.waDown': 'رقم الإرسال غير مربوط: تنتظر الإزالة حتى يُربط.',
  'rm.tab.todo': 'للإزالة',
  'rm.tab.done': 'أُزيلوا',
  'rm.selectAll': 'تحديد الكل',
  'rm.selectNone': 'إلغاء التحديد',
  'rm.selected': 'المحدَّدون: {n}',
  'rm.removeSelected': 'إزالة المحدَّدين',
  'rm.confirmTitle': 'إزالة المحدَّدين من المجموعة؟',
  'rm.confirmBody':
    'عدد المحدَّدين: {n}. سيُزيلهم رقم الإرسال من مجموعة «{group}» واحدًا كل بضع ثوانٍ، ويُسجَّلون «أُزيلوا». لإعادة أحدهم يجب إضافته إلى المجموعة يدويًا.',
  'rm.confirm': 'إزالة',
  'rm.started': 'بدأت الإزالة. عدد من في الانتظار: {n}',
  'rm.skipped': 'لم يُضف إلى الإزالة: {n} (أُزيل أو في الانتظار أو لم يعد عليه مبلغ).',
  'rm.queuedNote': 'جارٍ الإزالة. في الانتظار: {n}',
  'rm.cancel': 'إيقاف ما تبقّى',
  'rm.cancelled': 'أُوقفت الإزالة.',
  'rm.fines': 'منها غرامات: {amount}',
  'rm.status.QUEUED': 'في الانتظار',
  'rm.status.DONE': 'أُزيل',
  'rm.status.FAILED': 'تعذّرت الإزالة',
  'rm.status.CANCELLED': 'أُلغيت الإزالة',
  'rm.result.NOT_IN_GROUP': 'لم يكن في المجموعة',
  'rm.result.NOT_ADMIN': 'رقم الإرسال ليس مشرفًا في المجموعة',
  'rm.result.NO_GROUP': 'لم يُعثر على المجموعة',
  'rm.reinstate': 'إلغاء علامة الإزالة',
  'rm.reinstated': 'أصبح السائق نشطًا. أضِفه إلى المجموعة يدويًا إن لزم.',
  'rm.empty': 'لا يوجد من تلزم إزالته لهذا الأسبوع.',
  'rm.emptyDone': 'لم يُزَل أحد من هذا الأسبوع.',
  'rm.noWeeks': 'لم ينتهِ آخر موعد للدفع لأي أسبوع بعد.',
  'wa.group.title': 'مجموعة واتساب',
  'wa.group.hint': 'المجموعة التي تُزيل منها قائمة الإزالة. يجب أن يكون رقم الإرسال مشرفًا فيها.',
  'wa.group.none': 'لم تُحدَّد مجموعة بعد.',
  'wa.group.load': 'عرض مجموعات الرقم',
  'wa.group.change': 'تغيير المجموعة',
  'wa.group.choose': 'اختيار',
  'wa.group.members': 'عدد الأعضاء: {n}',
  'wa.group.saved': 'حُفظت المجموعة.',
  'wa.group.empty': 'رقم الإرسال ليس في أي مجموعة.',
  'act.group.set': 'تحديد مجموعة واتساب',
  'act.group.remove': 'طلب إزالة من المجموعة',
  'act.group.removeCancel': 'إيقاف الإزالة من المجموعة',
  'act.driver.groupRemoved': 'إزالة سائق من المجموعة',
  'err.NO_GROUP': 'لم تُحدَّد مجموعة واتساب، أو لم يُعثر عليها.',
  'err.NO_REMOVAL_WEEK': 'لم ينتهِ آخر موعد للدفع لهذا الأسبوع بعد.',
  'err.GROUPS_FAILED': 'تعذّر جلب المجموعات من واتساب.',
  'err.WA_DOWN': 'رقم الإرسال غير مربوط.',
  'del.button': 'حذف الأسبوع',
  'del.title': 'حذف جرد {tab} كاملًا؟',
  'del.body':
    'يُحذف الأسبوع بكل نسخه وكشوفه، ومعه الدفعات والاعتراضات والغرامات والرسائل المسجلة له وكشوف المنتجين. ويُحذف كذلك السائقون والمنتجون الذين لا يظهرون في أي أسبوع آخر.',
  'del.versions': 'نسخ الملف',
  'del.drivers': 'كشوف السائقين',
  'del.producers': 'كشوف المنتجين',
  'del.payments': 'دفعات',
  'del.disputes': 'اعتراضات',
  'del.fines': 'غرامات',
  'del.messages': 'رسائل',
  'del.realData': 'لهذا الأسبوع دفعات أو رسائل مسجلة. تأكد أنه أسبوع تجريبي قبل الحذف.',
  'del.type': 'للتأكيد، اكتب رقم الأسبوع: {tab}',
  'del.noUndo': 'لا يمكن التراجع عن الحذف إلا من النسخة الاحتياطية الليلية.',
  'del.confirm': 'حذف نهائي',
  'del.done': 'حُذف جرد {tab}.',
  'act.week.delete': 'حذف أسبوع',
  'err.CONFIRM_MISMATCH': 'رقم الأسبوع المكتوب لا يطابق.',
  'nav.producers': 'المنتجون',
  'pr.hint': 'كل ورقة في ملف الجرد غير أوراق «جرد» هي كشف منتج، وتُقرأ مع أحدث أسبوع. لا يُرسل لهم شيء إلا بزر «إرسال».',
  'pr.empty': 'لا توجد كشوف منتجين بعد. ارفع ملف الجرد وانشر أحدث أسبوع فيه، فتُقرأ أوراق المنتجين معه.',
  'pr.owedTotal': 'المستحق للمنتجين',
  'pr.paidTotal': 'دُفع',
  'pr.leftTotal': 'المتبقي',
  'pr.count': 'عدد المنتجين: {n}',
  'pr.jobs': 'عدد الطلبات: {n}',
  'pr.noPhone': 'بلا رقم، فلا يُرسل له شيء',
  'pr.view': 'عرض الكشف',
  'pr.f.toReview': 'بانتظار المراجعة',
  'pr.f.notSent': 'لم يُرسل',
  'pr.f.unpaid': 'لم يُدفع',
  'pr.f.changed': 'تغيّر',
  'pr.reviewed': 'مراجَع',
  'pr.notReviewed': 'لم يُراجَع',
  'pr.reviewedChanged': 'تغيّر بعد المراجعة',
  'pr.notSent': 'لم يُرسل',
  'pr.sentChanged': 'تغيّر بعد الإرسال',
  'pr.paid': 'مدفوع',
  'pr.unpaid': 'لم يُدفع',
  'pr.paidChanged': 'تغيّر بعد الدفع',
  'pr.nothingDue': 'لا مبلغ له',
  'pr.doReview': 'تمت المراجعة',
  'pr.undoReview': 'إلغاء المراجعة',
  'pr.reReview': 'تمت المراجعة مجددًا',
  'pr.reviewSaved': 'سُجّلت المراجعة.',
  'pr.reviewUndone': 'أُلغيت المراجعة.',
  'pr.send': 'إرسال',
  'pr.sendAgain': 'إرسال مجددًا',
  'pr.queued': 'في انتظار الإرسال',
  'pr.sendTitle': 'إرسال الكشف إلى {name}',
  'pr.sendTo': 'عبر واتساب إلى الرقم {phone}',
  'pr.sendNow': 'إرسال الآن',
  'pr.sendQueued': 'أُضيفت الرسالة إلى قائمة الإرسال.',
  'pr.sendNotReviewed': 'لم تُسجَّل مراجعة هذا الكشف بعد.',
  'pr.sendWaDown': 'واتساب غير مربوط الآن: ستنتظر الرسالة حتى يُربط الرقم.',
  'pr.phoneHint':
    'رقم الإرسال غير مربوط، فأرسِل الكشف من واتساب هاتفك. في الرسالة رابط دخول يعمل مرة واحدة دون رمز، ويظهر الكشف في صفحته بعد ضغطك على الزر.',
  'pr.phoneSent': 'سُجِّل الكشف مُرسَلًا، وأصبح ظاهرًا في صفحته.',
  'pr.markPaid': 'تم الدفع',
  'pr.paidTitle': 'تسجيل دفع المستحق لـ {name}',
  'pr.paidAmount': 'المبلغ المستحق',
  'pr.paidNote': 'ملاحظة (اختياري)',
  'pr.paidNoteHint': 'مثلًا: طريقة التحويل أو رقم الحوالة.',
  'pr.paidSaved': 'سُجّل الدفع.',
  'pr.undoPaid': 'إلغاء الدفع',
  'pr.undoPaidTitle': 'إلغاء تسجيل الدفع؟',
  'pr.undoPaidBody': 'يعود الكشف إلى «لم يُدفع».',
  'pr.paidUndone': 'أُلغي تسجيل الدفع.',
  'pr.template': 'نص الرسالة',
  'pr.templateHint': 'يُستخدم عند الضغط على «إرسال» لأي منتج.',
  'pr.templateSaved': 'حُفظ النص.',
  'pr.notes': 'ملاحظات في الورقة',
  'pr.notesHint': 'أسطر فيها نص بلا مبلغ، فلا تدخل في الحساب ولا يراها المنتج.',
  'pr.statement': 'كشف إنتاج',
  'pr.checks': 'المتابعة',
  'pr.messages': 'الرسائل المرسلة',
  'pr.weeks': 'الأسابيع',
  'pr.profile': 'البيانات',
  'pr.displayName': 'الاسم في الرسالة',
  'pr.displayNameHint': 'اتركه فارغًا لاستخدام الاسم في الورقة: {name}',
  'pr.notesField': 'ملاحظات للمحاسب',
  'pr.link': 'الرابط الشخصي',
  'pr.copyLink': 'نسخ الرابط',
  'pr.sheet': 'ورقة «{sheet}»',
  'pr.paidNoteShown': 'ملاحظة الدفع: {note}',
  'pr.status.reviewed': 'المراجعة',
  'pr.status.sent': 'الإرسال',
  'pr.status.paid': 'الدفع',
  'send.var.count': 'عدد الطلبات',
  'up.producers': 'أوراق المنتجين',
  'up.producersHint': 'عدد المنتجين: {n} · عدد الطلبات: {jobs} · المستحق لهم: {total} دينار',
  'up.producersOld': 'هذا ليس أحدث أسبوع في الملف، لذا لن تُقرأ أوراق المنتجين.',
  'up.sheet': 'ورقة «{sheet}»',
  'act.producer.review': 'مراجعة كشف منتج',
  'act.producer.unreview': 'إلغاء مراجعة كشف منتج',
  'act.producer.paid': 'تسجيل دفع لمنتج',
  'act.producer.unpaid': 'إلغاء دفع لمنتج',
  'act.producer.send': 'إرسال كشف منتج',
  'act.producer.update': 'تعديل بيانات منتج',
  'act.producer.login': 'دخول منتج',
  'pr.pw.title': 'الدخول بكلمة مرور',
  'pr.pw.hint': 'لمن يستخدم هاتفه أكثر من شخص: يُطلب منه كلمة المرور قبل رمز واتساب، ويبقى دخوله يومًا واحدًا فقط.',
  'pr.pw.phone': 'رقم رمز الدخول',
  'pr.pw.phoneHint': 'لا يوجد رقم في الورقة. يُرسَل إلى هذا الرقم رمز الدخول فقط عندما يطلبه، ولا تُرسَل إليه الكشوف أبدًا.',
  'pr.pw.password': 'كلمة المرور',
  'pr.pw.none': 'لا توجد كلمة مرور. يكفي رمز واتساب للدخول.',
  'pr.pw.set': 'كلمة المرور مفعّلة منذ {date}.',
  'pr.pw.create': 'إنشاء كلمة مرور',
  'pr.pw.renew': 'كلمة مرور جديدة',
  'pr.pw.renewTitle': 'إنشاء كلمة مرور جديدة؟',
  'pr.pw.renewBody': 'تتوقف كلمة المرور الحالية، ويُسجَّل خروجه من كل الأجهزة.',
  'pr.pw.remove': 'إزالة كلمة المرور',
  'pr.pw.removeTitle': 'إزالة كلمة المرور؟',
  'pr.pw.removeBody': 'سيكفي رمز واتساب وحده للدخول، ويستطيع كل من يستخدم الهاتف رؤية الكشف.',
  'pr.pw.removed': 'أُزيلت كلمة المرور.',
  'pr.pw.needPhone': 'أدخل رقم رمز الدخول أولًا.',
  'pr.pw.reviewShows': 'لا تُرسَل إليه الكشوف، لذلك يظهر الأسبوع في صفحته بعد أن تراجعه.',
  'pr.pw.shownTitle': 'كلمة المرور الجديدة',
  'pr.pw.shownBody': 'تظهر مرة واحدة فقط. انسخها وسلّمها له بنفسك، لا عبر الهاتف المشترك.',
  'pr.pw.copy': 'نسخ',
  'pr.pw.done': 'تم',
  'act.producer.password': 'كلمة مرور جديدة لمنتج',
  'act.producer.unpassword': 'إزالة كلمة مرور منتج',
  'act.producer.loginPhone': 'تعديل رقم دخول منتج',
  'err.NO_PHONE': 'لا يوجد رقم هاتف لهذا المنتج.',
  'err.ALREADY_QUEUED': 'الرسالة في انتظار الإرسال بالفعل.',
  'err.NO_PRODUCER': 'المنتج غير موجود.',
  'err.NO_PRODUCER_WEEK': 'لا توجد كشوف منتجين لهذا الأسبوع.',
} as const;

export type Key = keyof typeof ar;

const en: Record<Key, string> = {
  brand: 'JABAL VIP',
  'nav.week': 'Week',
  'nav.drivers': 'Drivers',
  'nav.upload': 'Upload',
  'nav.settings': 'Settings',
  owes: 'Owes',
  owed: 'Owed',
  even: 'Zero',
  jd: 'JD',
  loading: 'Loading…',
  retry: 'Try again',
  back: 'Back',
  save: 'Save',
  saved: 'Saved',
  cancel: 'Cancel',
  'err.generic': 'Something went wrong. Try again.',
  'err.BAD_LOGIN': 'Wrong phone number or password.',
  'err.BAD_PHONE': 'That phone number is not valid.',
  'err.TOO_MANY_TRIES': 'Too many attempts. Wait 15 minutes.',
  'err.TOO_MANY_CODES': 'Too many codes requested. Wait an hour.',
  'err.BAD_CODE': 'Wrong code.',
  'err.CODE_EXPIRED': 'The code expired. Go back and start again.',
  'err.NOT_A_WORKBOOK': 'This is not an Excel file (.xlsx).',
  'err.NO_WEEK_TABS': 'No «جرد» tab found in this file.',
  'err.FILE_TOO_BIG': 'The file is larger than 25 MB.',
  'err.EMPTY_FILE': 'The file is empty.',
  'err.BAD_TAB': "This tab couldn't be read.",
  'err.ALREADY_PUBLISHED': 'This version is already published.',
  'err.NOT_PREVIEWED': 'Check the file before publishing.',
  'err.WEEK_CLOSED': 'This week is closed.',
  'err.NOT_LOGGED_IN': 'Your session ended. Log in again.',
  'err.SERVER_DOWN': 'The server is not running. On the dev PC: check that pnpm dev is running with no error in the terminal.',
  'err.OFFLINE': 'No internet connection.',

  'login.title': 'Accountant login',
  'login.phone': 'Phone number',
  'login.password': 'Password',
  'login.continue': 'Continue',
  'login.codeTitle': 'WhatsApp code',
  'login.codeSent': 'We sent a 6-digit code on WhatsApp to the number ending in {n}.',
  'login.code': 'Code',
  'login.verify': 'Log in',
  'login.devCode': 'Development mode — the code is {code}',

  'week.title': 'Week {tab}',
  'week.range': 'Thu {from} → Thu {to}',
  'week.status.DRAFT': 'Draft',
  'week.status.PUBLISHED': 'Published',
  'week.status.CLOSED': 'Closed',
  'week.version': 'version {v}',
  'week.deadline': 'Payment deadline',
  'week.editDeadline': 'Change',
  'week.oweTotal': 'Drivers owe',
  'week.owedTotal': 'Owed to drivers',
  'week.fees': 'Weekly fees',
  'week.drivers': '{n} drivers',
  'week.emptyTitle': 'Start with the first week',
  'week.empty': 'No weeks yet. Upload the workbook to start.',
  'week.notPublished': 'This week is not published yet.',
  'week.upload': 'Upload the workbook',
  'week.reupload': 'Upload a corrected version',
  'week.problems': '{n} notes in the file',
  'week.changed': '{n} changed in the last version',
  'week.otherWeeks': 'Other weeks',
  'filter.all': 'All',
  'filter.owes': 'Owe',
  'filter.owed': 'Owed',
  'filter.fee': 'Took jobs',
  'filter.changed': 'Changed',
  'filter.problems': 'Notes',
  search: 'Search by name or number…',
  skip: 'Skip to content',
  'search.none': 'No results.',
  'list.lines': '{n} jobs',
  'list.exempt': 'exempt',
  'list.removed': 'removed',

  'st.title': 'Statement',
  'st.week': 'Week {tab} · {from} – {to}',
  'st.credit': 'Supplied',
  'st.debit': 'Took',
  'st.fee': 'Weekly fee',
  'st.total': 'Total',
  'st.toPay': 'To pay',
  'st.toReceive': 'To receive',
  'st.zero': 'Nothing due',
  'st.row': 'row {n}',
  'st.noDetails': 'no details',
  'st.driverPage': 'Driver page',
  'st.sheetStatus': 'status: {s}',

  'drv.title': 'All drivers',
  'drv.weeks': 'Weeks',
  'drv.noWeeks': 'No published statements.',
  'drv.profile': 'Driver details',
  'drv.nameInSheet': 'Name in the sheet: {name}',
  'drv.displayName': 'Display name',
  'drv.displayNameHint': 'Empty = the name in the sheet',
  'drv.feeExempt': 'No weekly fee (admin)',
  'drv.removed': 'Removed from the group',
  'drv.notes': 'Notes (the driver never sees them)',
  'drv.whatsapp': 'WhatsApp',

  'up.title': 'Upload the workbook',
  'up.pick': 'Choose the Excel file',
  'up.hint': 'Upload the whole file as it is. Only the «جرد» tabs are read.',
  'up.reading': 'Reading the file…',
  'up.whichWeek': 'Which week?',
  'up.newest': 'newest',
  'up.published': 'published — this will publish a corrected version',
  'up.check': 'Check the rows',
  'up.checking': 'Checking the rows…',
  'up.lines': '{n} rows with an amount',
  'up.empty': '{n} rows without an amount',
  'up.rosterOnly': '{n} listed with no amount this week (the fee only)',
  'up.newDrivers': '{n} new drivers',
  'up.problems': 'Before you publish',
  'up.clean': 'The file is clean, no notes.',
  'up.sev.error': 'Important',
  'up.sev.warning': 'Check',
  'up.sev.info': 'FYI',
  'up.rows': 'row {rows}',
  'up.more': 'and {n} more',
  'up.changes': 'What changed from the published version',
  'up.noChanges': 'Exactly the same as the published version.',
  'up.change.added': 'new',
  'up.change.removed': 'gone',
  'up.change.changed': 'changed',
  'up.deadline': 'Payment deadline',
  'up.publish': 'Publish the statements',
  'up.publishing': 'Publishing…',
  'up.done': 'Statements published',
  'up.doneBody': 'Week {tab} statements are ready (version {v}). Sending them on WhatsApp comes in a later stage.',
  'up.viewWeek': 'Open the week',
  'up.another': 'Upload another file',
  'up.change': 'change',

  'set.title': 'Settings',
  'set.money': 'Money',
  'set.visibility': 'What drivers and producers see',
  'set.oldWeeks': 'Show older statements',
  'set.oldWeeksHint':
    'When off, each of them sees the newest week only, even if a driver still owes for an older week (settle that from the driver page here).',
  'set.fee': 'Weekly fee',
  'set.feeHint': 'For every number in the week tab. Admins and producers are exempt.',
  'set.deadline': 'Payment deadline',
  'set.deadlineHint':
    'After the week closes (Thursday 12:00 noon). Saving applies it to every week whose deadline has not passed yet, and to new weeks.',
  'set.autoFine': 'Automatic late fine',
  'set.autoFineHint': 'Copied onto each week when it is first published; change it per week from the week bar. 0 = none.',
  'set.payMethods': 'Payment methods',
  'set.payMethodsHint': 'What drivers see when they pay.',
  'set.addMethod': 'Add a payment method',
  'set.type.CLIQ': 'CliQ',
  'set.type.ZAIN_CASH': 'Zain Cash',
  'set.type.ORANGE_MONEY': 'Orange Money',
  'set.type.OTHER': 'Other',
  'set.label': 'Name',
  'set.value': 'Alias or number',
  'set.shown': 'Shown to drivers',
  'set.hidden': 'Hidden',
  'set.hide': 'Hide',
  'set.show': 'Show',
  'set.delete': 'Delete',
  'set.deleteTitle': 'Delete the payment method «{label}»?',
  'set.deleteBody': 'Drivers will no longer see it. You can hide it instead of deleting it.',
  'set.up': 'Move up',
  'set.appearance': 'Look & language',
  'set.dark': 'Dark mode',
  'set.english': 'العربية',
  'set.activity': 'Activity log',
  'set.account': 'Account',
  'set.logout': 'Log out',
  'day.0': 'Thursday',
  'day.1': 'Friday',
  'day.2': 'Saturday',
  'day.3': 'Sunday',
  'day.4': 'Monday',
  'day.5': 'Tuesday',
  'day.6': 'Wednesday',
  'day.7': 'Next Thursday',

  'act.title': 'Activity log',
  'act.more': 'Older',
  'act.staff.add': 'Staff account added',
  'act.staff.login': 'Logged in',
  'act.staff.password_reset': 'Password reset',
  'act.upload.create': 'Uploaded a file',
  'act.upload.preview': 'Checked a tab',
  'act.week.publish': 'Published statements',
  'act.week.deadline': 'Changed the deadline',
  'act.driver.update': 'Edited a driver',
  'act.settings.update': 'Changed settings',
  'act.paymethod.add': 'Added a payment method',
  'act.paymethod.update': 'Edited a payment method',
  'act.paymethod.delete': 'Deleted a payment method',
  'nav.home': 'Home',
  'nav.review': 'Review',
  'nav.search': 'Search',
  'status.NONE': 'Nothing due',
  'status.PAID': 'Paid',
  'status.SUBMITTED': 'Proof sent',
  'status.DISPUTED': 'Disputed',
  'status.LATE': 'Late',
  'status.UNPAID': 'Unpaid',
  'home.title': 'Overview',
  'home.collected': 'Collected',
  'home.remaining': 'Still to collect',
  'home.progress': '{a} of {b} drivers paid',
  'home.todo': 'Waiting for you',
  'home.proofs': 'Payment proofs to review',
  'home.disputes': 'Open disputes',
  'home.late': 'Drivers late to pay',
  'home.unpaid': 'Not paid yet',
  'home.notes': 'Notes in the workbook',
  'home.allClear': 'Nothing is waiting for you.',
  'home.quick': 'Shortcuts',
  'home.allDrivers': 'Drivers table',
  'tbl.scope': 'Scope',
  'tbl.all': 'All drivers (all weeks)',
  'tbl.weekOption': 'Week {tab} · {from}',
  'tbl.name': 'Driver',
  'tbl.status': 'Status',
  'tbl.lines': 'Lines',
  'tbl.credit': 'Supplied',
  'tbl.debit': 'Took',
  'tbl.fee': 'Fee',
  'tbl.total': 'Total',
  'tbl.remaining': 'Remaining',
  'tbl.outstanding': 'Owes (all weeks)',
  'tbl.lastWeek': 'Last week',
  'tbl.weeks': 'Weeks',
  'tbl.actions': 'Actions',
  'tbl.sort': 'Sort',
  'tbl.count': '{n} results',
  'tbl.clear': 'Clear filters',
  'tbl.lateWeeks': 'Late weeks: {n}',
  'sort.amount': 'Amount (highest first)',
  'sort.name': 'Name',
  'sort.status': 'Status',
  'sort.lines': 'Number of lines',
  'f.unpaid': 'Unpaid',
  'f.submitted': 'Proof sent',
  'f.paid': 'Paid',
  'f.late': 'Late',
  'f.disputed': 'Disputed',
  'f.exempt': 'Exempt',
  'f.removed': 'Removed',
  'f.outstanding': 'Owe money',
  'row.statement': 'Open statement',
  'row.profile': 'Driver page',
  'row.whatsapp': 'Message on WhatsApp',
  'row.copyPhone': 'Copy number',
  'ms.open': 'Send from my phone',
  'ms.title': 'Send from your phone',
  'ms.statement': 'Statements',
  'ms.reminder': 'Reminder',
  'ms.how':
    "Tap «Send on WhatsApp»: WhatsApp opens with the message ready. Send it there and come back here; the next one is ready. Every message has a one-time login link, so drivers need no code. Send in batches with breaks, so your number isn't banned.",
  'ms.progress': 'Sent: {n} of {total}',
  'ms.showText': 'Message text',
  'ms.send': 'Send on WhatsApp',
  'ms.skip': 'Skip',
  'ms.skipped': 'Skipped',
  'ms.sent': 'Sent',
  'ms.sentBefore': 'Sent before',
  'ms.done': 'Everyone has the message.',
  'ms.nobody': 'Nobody needs this message this week.',
  'ms.skippedLeft': 'Skipped: {n}',
  'ms.backToSkipped': 'Back to the skipped ones',
  'ms.everyone': 'Everyone',
  'sl.title': 'Staff logins',
  'sl.hint':
    "When the login code can't go out on WhatsApp, send the staff member a one-time login link; they log in with it and their password.",
  'sl.link': 'Login link',
  'sl.ACCOUNTANT': 'Accountant',
  'sl.SUPER_ADMIN': 'Admin',
  'll.bodyStaff': 'Logs them into the dashboard with their password, no WhatsApp code. Works once only and expires {until}.',
  'll.messageStaff': 'رابط الدخول إلى لوحة JABAL VIP (يعمل مرة واحدة، مع كلمة مرورك):\n{url}',
  'err.NOT_A_RECIPIENT': "This driver isn't among this message's recipients.",
  'err.NO_STAFF': "This account doesn't exist.",
  'act.staff.loginLink': 'Made a staff login link',
  'll.make': 'One-time login link',
  'll.title': 'Login link: {name}',
  'll.body': 'Logs them straight in, no WhatsApp code. Works once only and expires {until}. Making a new link cancels this one.',
  'll.link': 'Link',
  'll.sendTo': 'Send it to:',
  'll.copy': 'Copy link',
  'll.copied': 'Login link copied.',
  'll.whatsapp': 'Send on WhatsApp',
  'll.message': 'رابط الدخول إلى كشفك في JABAL VIP (يعمل مرة واحدة):\n{url}',
  'act.driver.loginLink': 'Made a driver login link',
  'act.producer.loginLink': 'Made a producer login link',
  'row.copyLink': 'Copy driver link',
  'row.cash': 'Record cash payment',
  copied: 'Copied.',
  'link.copied': 'Driver link copied. Send it on WhatsApp.',
  'link.regenerate': 'New link',
  'link.regenerateBody': 'The old link will stop working. Use this if the link reached someone else.',
  'link.regenerated': 'New link created and copied.',
  'cash.title': 'Record a cash payment',
  'cash.body': '{name} paid {amount} JD for week {tab}.',
  'cash.note': 'Note (optional)',
  'cash.confirm': 'Record payment',
  'cash.done': 'Payment recorded.',
  'rev.proofs': 'Payment proofs',
  'rev.disputes': 'Disputes',
  'rev.waiting': 'Waiting',
  'rev.history': 'History',
  'rev.emptyProofs': 'No proofs waiting.',
  'rev.emptyDisputes': 'No open disputes.',
  'rev.emptyHistory': 'No history yet.',
  'rev.pick': 'Pick an item from the list to review it.',
  'rev.amount': 'Amount',
  'rev.method': 'Method',
  'rev.reference': 'Reference',
  'rev.sentAt': 'Sent',
  'rev.approve': 'Approve',
  'rev.reject': 'Reject',
  'rev.reason': 'Reason',
  'rev.reasonPlaceholder': 'Write the reason the driver will see…',
  'rev.r1': 'The image is not clear',
  'rev.r2': 'Wrong amount',
  'rev.r3': 'The transfer has not arrived',
  'rev.r4': 'This image was already used',
  'rev.reused': 'This image was used for another payment — check it.',
  'rev.approved': 'Payment approved.',
  'rev.rejected': 'Payment rejected.',
  'rev.reopen': 'Back to review',
  'rev.reopened': 'Payment is back in review.',
  'rev.openImage': 'Open full-size image',
  'rev.line': 'Disputed line',
  'rev.message': "Driver's message",
  'rev.reply': 'Reply to the driver',
  'rev.replyPlaceholder': 'Write the reply the driver will see…',
  'rev.accept': 'Accept',
  'rev.rejectDispute': 'Reject',
  'rev.acceptHint': 'After accepting: fix the Excel file and upload it again so the statement changes.',
  'rev.answered': 'Reply sent.',
  'rev.keys': 'Keyboard shortcuts',
  'rev.photo': 'Attached photo',
  'pstatus.SUBMITTED': 'Waiting',
  'pstatus.APPROVED': 'Approved',
  'pstatus.REJECTED': 'Rejected',
  'dstatus.OPEN': 'Open',
  'dstatus.ACCEPTED': 'Accepted',
  'dstatus.REJECTED': 'Rejected',
  'dstatus.WITHDRAWN': 'Withdrawn',
  'method.CASH': 'Cash',
  'method.CLIQ': 'CliQ',
  'method.ZAIN_CASH': 'Zain Cash',
  'method.ORANGE_MONEY': 'Orange Money',
  'method.OTHER': 'Other',
  'drv.tabs.weeks': 'Statements',
  'drv.tabs.payments': 'Payments',
  'drv.tabs.disputes': 'Disputes',
  'drv.tabs.profile': 'Details',
  'drv.outstanding': 'Owes across all weeks',
  'drv.noPayments': 'No payments.',
  'drv.noDisputes': 'No disputes.',
  'st.payment': 'Payment',
  'st.paidOf': '{paid} of {due} JD paid',
  'st.disputedLine': 'Disputed',
  'pal.placeholder': 'Search a driver by name or number, or a page…',
  'pal.pages': 'Pages',
  'pal.drivers': 'Drivers',
  'pal.weeks': 'Weeks',
  'pal.empty': 'No results.',
  'pal.title': 'Quick search',
  'nav.activity': 'Activity log',
  'tbl.copyPhones': 'Copy numbers',
  'tbl.export': 'Export CSV',
  'nav.send': 'Send statements',
  'nav.removal': 'Removal list',
  'send.title': 'Send on WhatsApp',
  'send.week': 'Week',
  'send.kind': 'Message',
  'send.kind.STATEMENT': 'Weekly statement',
  'send.kind.REMINDER': 'Payment reminder',
  'send.kind.DISPUTE_REPLY': 'Dispute answer',
  'send.audience': 'Recipients',
  'send.aud.ALL': 'Everyone',
  'send.aud.CHANGED': 'Changed only',
  'send.aud.UNPAID': 'Unpaid only',
  'send.audHint.ALL': 'Every driver with a statement this week.',
  'send.audHint.CHANGED': 'Those with no message yet, or whose total changed since their last one (e.g. after a re-upload).',
  'send.audHint.UNPAID': 'Those who owe, sent no proof and have no open dispute.',
  'send.reminderHint': 'A driver gets at most one reminder a week; never those who paid, sent a proof or have an open dispute.',
  'send.pace': 'Speed',
  'send.pace.SLOW': 'Slow (recommended)',
  'send.paceHint.SLOW': 'One message every 30–45 seconds, to keep the number from being banned.',
  'send.pace.FAST': 'Fast',
  'send.paceHint.FAST': 'One message every 3–5 seconds.',
  'send.fastWarn': 'WhatsApp may ban the number when many messages go out fast. Use fast only for a few drivers.',
  'send.template': 'Message text',
  'send.templateHint':
    'Tap a placeholder to add it. A line whose placeholder is empty is left out (e.g. the deadline for drivers who owe nothing).',
  'send.var.name': 'Name',
  'send.var.number': 'Phone number',
  'send.var.week': 'Week',
  'send.var.amount': 'Amount',
  'send.var.deadline': 'Deadline',
  'send.var.link': 'Link',
  'send.needsLink': "The text must include the driver's link.",
  'send.saveDefault': 'Save as default',
  'send.savedDefault': 'Default text saved.',
  'send.resetDefault': 'Back to the saved text',
  'send.preview': 'Preview',
  'send.previewFor': 'As {name} will get it',
  'send.count': 'Recipients: {n}',
  'send.minutes': 'About {n} min',
  'send.skipped': 'Already reminded this week, left out: {n}.',
  'send.none': 'Nobody to send to.',
  'send.start': 'Start sending',
  'send.confirmTitle': 'Start sending?',
  'send.confirmBody': '{n} messages, about {m} min. You can pause at any time.',
  'send.started': 'Sending started.',
  'send.now': 'Sending now',
  'send.progress': 'Sent {a} of {b}',
  'send.pause': 'Pause',
  'send.resume': 'Resume',
  'send.cancel': 'Cancel the rest',
  'send.retry': 'Retry failed',
  'send.continue': 'Continue for those not reached',
  'send.continueHint':
    "Not reached yet: {n}. Only they get it, with today's numbers; anyone who no longer needs it (e.g. paid since) is skipped.",
  'send.continued': 'Sending resumed.',
  'err.NOT_CANCELLED': 'This send was not cancelled.',
  'send.cancelTitle': 'Cancel the remaining messages?',
  'send.cancelBody':
    'Messages not sent yet stop; sent ones are not affected. You can continue later from Earlier sends: only those not reached get it.',
  'send.paused.DISCONNECTED': 'Paused: WhatsApp is disconnected. Reconnect the number, then press Resume.',
  'send.paused.FAILURES': 'Paused after several failures in a row. Check the errors, then press Resume.',
  'send.paused.STAFF': 'Sending is paused.',
  'send.b.RUNNING': 'Sending',
  'send.b.PAUSED': 'Paused',
  'send.b.DONE': 'Done',
  'send.b.CANCELLED': 'Cancelled',
  'send.m.QUEUED': 'Waiting',
  'send.m.SENDING': 'Sending',
  'send.m.SENT': 'Sent',
  'send.m.DELIVERED': 'Delivered',
  'send.m.READ': 'Read',
  'send.m.FAILED': 'Failed',
  'send.m.CANCELLED': 'Cancelled',
  'send.e.NOT_ON_WHATSAPP': 'Not on WhatsApp',
  'send.e.INTERRUPTED': 'Interrupted by a server restart',
  'send.e.other': 'Could not send',
  'send.history': 'Earlier sends',
  'send.historyEmpty': 'Nothing sent for this week yet.',
  'send.messages': 'Messages',
  'send.failedList': 'Failed messages',
  'send.wa.open': 'WhatsApp connected',
  'send.wa.down': 'WhatsApp is not connected: nothing will be sent until it is back.',
  'send.wa.console': 'Development mode: messages are printed in the server log, not sent.',
  'wa.title': 'Sending number',
  'wa.hint': 'The number login codes, statements and reminders reach the drivers from.',
  'wa.open': 'Manage the sending number',
  'wa.status': 'Link status',
  'wa.linked': 'Linked',
  'wa.notLinked': 'Not linked',
  'wa.number': 'Number',
  'wa.name': 'WhatsApp name',
  'wa.downTitle': 'WhatsApp is not linked',
  'wa.downHint': 'Nothing reaches the drivers right now, and login codes reach neither the accountant nor the drivers.',
  'wa.linkNow': 'Link WhatsApp now',
  'wa.unlink': 'Unlink',
  'wa.unlinkTitle': 'Unlink the sending number?',
  'wa.unlinkBody': 'No login codes, statements or reminders go out until a number is linked again, and any send in progress stops.',
  'wa.unlinked': 'The number was unlinked.',
  'wa.linkTitle': 'Link the number',
  'wa.linkHint': 'Keep the phone with the sending number at hand, then choose how to link.',
  'wa.byCode': 'With a pairing code',
  'wa.byQr': 'By scanning a QR',
  'wa.phone': 'Sending number',
  'wa.phoneHint': 'The number registered in WhatsApp on that phone.',
  'wa.getCode': 'Get a pairing code',
  'wa.newCode': 'New code',
  'wa.getQr': 'Show the QR',
  'wa.copy': 'Copy the code',
  'wa.qrAlt': 'QR code to link WhatsApp',
  'wa.step1': 'Open WhatsApp on that phone, then: Settings → Linked devices → Link a device.',
  'wa.step2code': 'Choose "Link with phone number instead", then type the code shown above.',
  'wa.step2qr': 'Point the phone camera at the code shown above.',
  'wa.waiting': 'Waiting for the phone to finish linking…',
  'wa.expires': 'The code is short-lived; if it expires, get a new one.',
  'wa.done': 'WhatsApp is linked.',
  'login.serverLog':
    "The code couldn't go out on WhatsApp, so it was saved in the server log. Ask the admin for it; they show it by running this on the server:",
  'err.CODE_NOT_SENT': "The code couldn't be sent on WhatsApp right now. Please try later.",
  'err.ALREADY_LINKED': 'The number is already linked.',
  'err.DEV_SENDER': 'Development mode: there is no WhatsApp to link.',
  'err.WHATSAPP_UNREACHABLE': "The server's WhatsApp service can't be reached. Try again shortly.",
  'err.FORBIDDEN': 'Only the super admin can do this.',
  'send.noWeek': 'No published week yet. Publish the sheet first.',
  'home.notSent': 'Statements not sent yet',
  'home.toRemove': 'To remove from the group',
  'home.sending': 'Sending statements',
  'fine.add': 'Add a fine',
  'fine.title': 'Fine for {name}',
  'fine.amount': 'Amount (JD)',
  'fine.reason': 'Reason',
  'fine.reasonPh': 'e.g. cancelled a trip without notice',
  'fine.where': 'Added to the week {tab} statement; the driver sees it there.',
  'fine.added': 'Fine added.',
  'fine.cancel': 'Cancel the fine',
  'fine.cancelTitle': 'Cancel this fine?',
  'fine.cancelBody': 'It comes off what is due. A cancelled automatic fine is never added again.',
  'fine.cancelled': 'Fine cancelled.',
  'fine.line': 'Fine: {reason}',
  'fine.auto': 'Automatic',
  'fine.weekFine': 'Late fine for this week (JD)',
  'fine.weekFineHint': "Added automatically at the deadline to drivers who haven't paid, sent a proof or opened a dispute. 0 = none.",
  'set.messages': 'WhatsApp messages',
  'set.messagesHint': 'Message texts and speed are set on the send page.',
  'set.openSend': 'Open the send page',
  'f.notSent': 'Not sent',
  'err.BATCH_ACTIVE': 'Another send is in progress. Wait for it or cancel it first.',
  'err.NO_RECIPIENTS': 'Nobody to send to.',
  'err.BATCH_FINISHED': 'This send has finished.',
  'err.NOTHING_FAILED': 'No failed messages.',
  'err.NOT_PUBLISHED': 'This week is not published.',
  'err.NO_FINE': "The fine doesn't exist or was already cancelled.",
  'err.NO_STATEMENT': 'This driver has no statement that week.',
  'err.INVALID': 'Please check what you entered.',
  'rm.title': 'Removal list',
  'rm.hint':
    'Who still owes for a week once its payment deadline has passed. Select who to remove: the sending number removes them from the WhatsApp group, one every few seconds.',
  'rm.deadline': 'Deadline passed: {date}',
  'rm.group': 'Group: {name}',
  'rm.noGroup': 'No WhatsApp group chosen yet, so nobody can be removed. The super admin chooses it on the «Sending number» page.',
  'rm.noGroupAdmin': 'Choose the WhatsApp group first, on the «Sending number» page.',
  'rm.chooseGroup': 'Choose the group',
  'rm.waDown': 'The sending number is not linked: removals wait until it is.',
  'rm.tab.todo': 'To remove',
  'rm.tab.done': 'Removed',
  'rm.selectAll': 'Select all',
  'rm.selectNone': 'Clear selection',
  'rm.selected': 'Selected: {n}',
  'rm.removeSelected': 'Remove selected',
  'rm.confirmTitle': 'Remove the selected people from the group?',
  'rm.confirmBody':
    'Selected: {n}. The sending number removes them from «{group}», one every few seconds, and they are marked removed. To bring someone back, add them to the group by hand.',
  'rm.confirm': 'Remove',
  'rm.started': 'Removing. Waiting: {n}',
  'rm.skipped': 'Not added: {n} (already removed, already waiting, or no longer owing).',
  'rm.queuedNote': 'Removing. Waiting: {n}',
  'rm.cancel': 'Stop the rest',
  'rm.cancelled': 'Removals stopped.',
  'rm.fines': 'Fines included: {amount}',
  'rm.status.QUEUED': 'Waiting',
  'rm.status.DONE': 'Removed',
  'rm.status.FAILED': 'Could not remove',
  'rm.status.CANCELLED': 'Cancelled',
  'rm.result.NOT_IN_GROUP': 'Was not in the group',
  'rm.result.NOT_ADMIN': 'The sending number is not a group admin',
  'rm.result.NO_GROUP': 'Group not found',
  'rm.reinstate': 'Undo removed mark',
  'rm.reinstated': 'The driver is active again. Add them to the group by hand if needed.',
  'rm.empty': 'Nobody to remove for this week.',
  'rm.emptyDone': 'Nobody removed from this week.',
  'rm.noWeeks': 'No week has passed its payment deadline yet.',
  'wa.group.title': 'WhatsApp group',
  'wa.group.hint': 'The group the removal list removes people from. The sending number must be an admin there.',
  'wa.group.none': 'No group chosen yet.',
  'wa.group.load': "Show the number's groups",
  'wa.group.change': 'Change the group',
  'wa.group.choose': 'Choose',
  'wa.group.members': 'Members: {n}',
  'wa.group.saved': 'Group saved.',
  'wa.group.empty': 'The sending number is in no group.',
  'act.group.set': 'Chose the WhatsApp group',
  'act.group.remove': 'Asked to remove from the group',
  'act.group.removeCancel': 'Stopped removals',
  'act.driver.groupRemoved': 'Removed a driver from the group',
  'err.NO_GROUP': 'No WhatsApp group chosen, or it was not found.',
  'err.NO_REMOVAL_WEEK': "This week's payment deadline has not passed yet.",
  'err.GROUPS_FAILED': 'Could not get the groups from WhatsApp.',
  'err.WA_DOWN': 'The sending number is not linked.',
  'del.button': 'Delete week',
  'del.title': 'Delete week {tab} completely?',
  'del.body':
    'The week goes with all its versions and statements, and with the payments, disputes, fines and messages recorded for it, and the producer statements. Drivers and producers who appear in no other week are removed too.',
  'del.versions': 'File versions',
  'del.drivers': 'Driver statements',
  'del.producers': 'Producer statements',
  'del.payments': 'Payments',
  'del.disputes': 'Disputes',
  'del.fines': 'Fines',
  'del.messages': 'Messages',
  'del.realData': 'This week has payments or messages recorded. Make sure it is a test week before deleting.',
  'del.type': 'To confirm, type the week number: {tab}',
  'del.noUndo': 'This cannot be undone, except from the nightly backup.',
  'del.confirm': 'Delete for good',
  'del.done': 'Week {tab} deleted.',
  'act.week.delete': 'Deleted a week',
  'err.CONFIRM_MISMATCH': 'The week number typed does not match.',
  'nav.producers': 'Producers',
  'pr.hint':
    'Every tab of the workbook that is not a «جرد» tab is a producer’s statement, read with the newest week. Nothing is sent to them unless you press «Send».',
  'pr.empty': 'No producer statements yet. Upload the workbook and publish its newest week: the producer tabs are read with it.',
  'pr.owedTotal': 'Owed to producers',
  'pr.paidTotal': 'Paid',
  'pr.leftTotal': 'Left to pay',
  'pr.count': '{n} producers',
  'pr.jobs': '{n} jobs',
  'pr.noPhone': 'No phone: nothing can be sent',
  'pr.view': 'View statement',
  'pr.f.toReview': 'To review',
  'pr.f.notSent': 'Not sent',
  'pr.f.unpaid': 'Not paid',
  'pr.f.changed': 'Changed',
  'pr.reviewed': 'Reviewed',
  'pr.notReviewed': 'Not reviewed',
  'pr.reviewedChanged': 'Changed since review',
  'pr.notSent': 'Not sent',
  'pr.sentChanged': 'Changed since sent',
  'pr.paid': 'Paid',
  'pr.unpaid': 'Not paid',
  'pr.paidChanged': 'Changed since paid',
  'pr.nothingDue': 'Nothing due',
  'pr.doReview': 'Mark reviewed',
  'pr.undoReview': 'Undo review',
  'pr.reReview': 'Reviewed again',
  'pr.reviewSaved': 'Marked reviewed.',
  'pr.reviewUndone': 'Review undone.',
  'pr.send': 'Send',
  'pr.sendAgain': 'Send again',
  'pr.queued': 'Waiting to send',
  'pr.sendTitle': 'Send the statement to {name}',
  'pr.sendTo': 'On WhatsApp to {phone}',
  'pr.sendNow': 'Send now',
  'pr.sendQueued': 'The message is in the send queue.',
  'pr.sendNotReviewed': 'This statement is not marked reviewed yet.',
  'pr.sendWaDown': 'WhatsApp is not linked right now: the message waits until it is.',
  'pr.phoneHint':
    'The sending number is not linked, so send the statement from the WhatsApp on your phone. The message has a one-time login link (no code), and the statement shows on their page once you tap the button.',
  'pr.phoneSent': 'Recorded as sent; the statement now shows on their page.',
  'pr.markPaid': 'Mark paid',
  'pr.paidTitle': 'Record paying {name}',
  'pr.paidAmount': 'Amount owed',
  'pr.paidNote': 'Note (optional)',
  'pr.paidNoteHint': 'E.g. how it was transferred, or the transfer number.',
  'pr.paidSaved': 'Payment recorded.',
  'pr.undoPaid': 'Undo paid',
  'pr.undoPaidTitle': 'Undo the payment?',
  'pr.undoPaidBody': 'The statement goes back to «Not paid».',
  'pr.paidUndone': 'Payment undone.',
  'pr.template': 'Message text',
  'pr.templateHint': 'Used when you press «Send» for any producer.',
  'pr.templateSaved': 'Text saved.',
  'pr.notes': 'Notes in the tab',
  'pr.notesHint': 'Rows with text but no amount: not counted, and the producer doesn’t see them.',
  'pr.statement': 'Producer statement',
  'pr.checks': 'Follow-up',
  'pr.messages': 'Messages sent',
  'pr.weeks': 'Weeks',
  'pr.profile': 'Details',
  'pr.displayName': 'Name in the message',
  'pr.displayNameHint': 'Leave empty to use the name in the tab: {name}',
  'pr.notesField': 'Notes for the accountant',
  'pr.link': 'Personal link',
  'pr.copyLink': 'Copy link',
  'pr.sheet': 'Tab «{sheet}»',
  'pr.paidNoteShown': 'Payment note: {note}',
  'pr.status.reviewed': 'Review',
  'pr.status.sent': 'Sending',
  'pr.status.paid': 'Payment',
  'send.var.count': 'Jobs',
  'up.producers': 'Producer tabs',
  'up.producersHint': '{n} producers · {jobs} jobs · owed to them: {total} JD',
  'up.producersOld': 'This is not the newest week in the file, so the producer tabs are not read.',
  'up.sheet': 'Tab «{sheet}»',
  'act.producer.review': 'Reviewed a producer statement',
  'act.producer.unreview': 'Undid a producer review',
  'act.producer.paid': 'Recorded a producer payment',
  'act.producer.unpaid': 'Undid a producer payment',
  'act.producer.send': 'Sent a producer statement',
  'act.producer.update': 'Edited a producer',
  'act.producer.login': 'Producer logged in',
  'pr.pw.title': 'Password login',
  'pr.pw.hint':
    'For someone whose phone is used by others: a password is asked before the WhatsApp code, and the login lasts one day only.',
  'pr.pw.phone': 'Login code number',
  'pr.pw.phoneHint': 'The tab has no number. Only login codes go to this number, when they ask for one; statements never do.',
  'pr.pw.password': 'Password',
  'pr.pw.none': 'No password. The WhatsApp code alone logs in.',
  'pr.pw.set': 'Password on since {date}.',
  'pr.pw.create': 'Create a password',
  'pr.pw.renew': 'New password',
  'pr.pw.renewTitle': 'Make a new password?',
  'pr.pw.renewBody': 'The current password stops working and they are logged out everywhere.',
  'pr.pw.remove': 'Remove password',
  'pr.pw.removeTitle': 'Remove the password?',
  'pr.pw.removeBody': 'The WhatsApp code alone will log in, so anyone using the phone can see the statement.',
  'pr.pw.removed': 'Password removed.',
  'pr.pw.needPhone': 'Enter the login code number first.',
  'pr.pw.reviewShows': 'Statements are never sent to them, so a week shows on their page once you review it.',
  'pr.pw.shownTitle': 'The new password',
  'pr.pw.shownBody': 'Shown once only. Copy it and give it to them yourself, not through the shared phone.',
  'pr.pw.copy': 'Copy',
  'pr.pw.done': 'Done',
  'act.producer.password': 'New producer password',
  'act.producer.unpassword': 'Removed a producer password',
  'act.producer.loginPhone': 'Changed a producer login number',
  'err.NO_PHONE': 'This producer has no phone number.',
  'err.ALREADY_QUEUED': 'The message is already waiting to be sent.',
  'err.NO_PRODUCER': 'Producer not found.',
  'err.NO_PRODUCER_WEEK': 'No producer statements for this week.',
};

const DICTS: Record<Lang, Record<Key, string>> = { ar, en };

interface I18n {
  lang: Lang;
  t: (key: Key, vars?: Record<string, string | number>) => string;
  /** for keys built at runtime (error codes, activity actions): falls back to `fallback` */
  tr: (key: string, fallback: string, vars?: Record<string, string | number>) => string;
  setLang: (l: Lang) => void;
}
const Ctx = createContext<I18n | null>(null);

const fill = (s: string, vars?: Record<string, string | number>) => (vars ? s.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? '')) : s);

export function I18nProvider({ children, fixed }: { children: ReactNode; fixed?: Lang }) {
  const [lang, setLangState] = useState<Lang>(() => fixed ?? (document.documentElement.lang === 'en' ? 'en' : 'ar'));
  const setLang = useCallback((l: Lang) => {
    document.documentElement.lang = l;
    document.documentElement.dir = l === 'ar' ? 'rtl' : 'ltr';
    try {
      localStorage.setItem('jv.lang', l);
    } catch {}
    setLangState(l);
  }, []);
  const value = useMemo<I18n>(
    () => ({
      lang,
      setLang,
      t: (key, vars) => fill(DICTS[lang][key] ?? key, vars),
      tr: (key, fallback, vars) => fill((DICTS[lang] as Record<string, string>)[key] ?? fallback, vars),
    }),
    [lang, setLang],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18n {
  const c = useContext(Ctx);
  if (!c) throw new Error('useI18n outside I18nProvider');
  return c;
}

/** The message to show for an API error code (errors.* keys), else the generic one. */
export function errorText(i: I18n, e: unknown): string {
  const code = e && typeof e === 'object' && 'code' in e ? String((e as { code: unknown }).code) : '';
  return i.tr(`err.${code}`, i.t('err.generic'));
}
